import React, { useEffect } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import styles from './SidebarCardContainer.module.scss';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { translationsState } from '../../../../core/atoms/translation/translation.atom';
import { translate } from '../../../../core/utils/translation/translation';
import upgradeIcon from '../../../../assets/icons/upgrade.svg';
import grmrIcon from '../../../../assets/icons/grmr.svg';
import rocketIcon from '../../../../assets/icons/rocket.svg';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../config/routes/routes';
import { authTokenState } from '../../../../core/atoms/auth/auth.atom';
import { RoleType } from '../../../../core/domain/Role';
import { useMutation } from '@tanstack/react-query';
import axios, { SuccessResponse } from '../../../../core/libraries/axios/axios';
import { DefaultError } from '@tanstack/query-core/build/modern/index';
import { apiRoutes } from '../../../../config/api/apiRoutes';
import { currentPlanState } from '../../../atoms/client/client.atom';
import { PlanType } from '../../../domain/Plan';
import { sidebarExtendedState } from '../../../../core/atoms/layout/layout.atom';
import cx from 'classnames';

const version = process.env.REACT_APP_VERSION ?? '';

export const SidebarCardContainer = () => {
  const translations = useRecoilValue(translationsState);
  const authStateValue = useRecoilValue(authTokenState);
  const currentPlan = useRecoilValue(currentPlanState);
  const setCurrentPlanState = useSetRecoilState(currentPlanState);
  const sidebarExtendedValue = useRecoilValue(sidebarExtendedState);

  const navigate = useNavigate();

  const { mutate: getCurrentPlan } = useMutation<
    SuccessResponse<PlanType>,
    DefaultError
  >({
    mutationFn: () => axios.get(apiRoutes.plans.getCurrent),
    onSuccess: (response) => {
      setCurrentPlanState(response.data);
    },
  });

  useEffect(() => {
    getCurrentPlan();
  }, []);

  if (
    !authStateValue?.roles?.includes(RoleType.USER) &&
    !authStateValue?.roles?.includes(RoleType.USER_LIMITED)
  ) {
    return null;
  }

  return (
    <Stack className={styles.sidebarCardContainer}>
      {currentPlan &&
        currentPlan === PlanType.BASIC &&
        authStateValue?.roles?.includes(RoleType.USER) && (
          <Stack
            className={cx(styles.becomePremiumContainer, {
              [styles.small]: !sidebarExtendedValue,
            })}
          >
            <img className={styles.icon} src={upgradeIcon} />
            <Typography
              className={cx(styles.title, {
                [styles.small]: !sidebarExtendedValue,
              })}
            >
              {translate(translations, 'PLAN_SIDEBAR.BECOME_PREMIUM')}
            </Typography>
            {sidebarExtendedValue && (
              <Typography className={styles.text}>
                {translate(
                  translations,
                  'PLAN_SIDEBAR.BECOME_PREMIUM_DESCRIPTION',
                )}
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={
                sidebarExtendedValue ? <img src={rocketIcon} /> : undefined
              }
              onClick={() => navigate(routes.admin.plans)}
              className={sidebarExtendedValue ? undefined : styles.button}
            >
              {sidebarExtendedValue ? (
                translate(translations, 'PLAN_SIDEBAR.BECOME_PREMIUM_BUTTON')
              ) : (
                <img src={rocketIcon} />
              )}
            </Button>
          </Stack>
        )}
      <Stack
        className={cx(styles.getExtensionContainer, {
          [styles.small]: !sidebarExtendedValue,
        })}
      >
        <Tooltip title={version} placement="top">
          <Box
            tabIndex={0}
            aria-label={`gr.mr ${version}`}
            style={{ display: 'flex' }}
          >
            <img src={grmrIcon} />
          </Box>
        </Tooltip>

        <Box>
          {sidebarExtendedValue && (
            <Typography className={styles.title}>
              {translate(translations, 'PLAN_SIDEBAR.GET_EXTENSION_TITLE')}
            </Typography>
          )}
          <a
            href={process.env.REACT_APP_CHROME_EXTENSION_URL}
            className={cx(styles.link, {
              [styles.small]: !sidebarExtendedValue,
            })}
            target={'_blank'}
            rel="noopener noreferrer"
          >
            {translate(translations, 'PLAN_SIDEBAR.GET_EXTENSION_INSTALL')}
          </a>
        </Box>
      </Stack>
    </Stack>
  );
};

export default SidebarCardContainer;
